import { useState, useEffect } from 'react';
import { fetchBannersBySection } from '../services/bannerService';

const CACHE_SCRIPT_ID = 'flowdo-banner-cache';
const STORAGE_KEY = 'flowdo-banner-cache';
const bannerCache = new Map();
let cachesHydrated = false;

function applyParsedCache(parsed) {
  Object.entries(parsed || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) bannerCache.set(key, value);
  });
}

function hydrateCaches() {
  if (cachesHydrated || typeof document === 'undefined') return;
  cachesHydrated = true;

  const script = document.getElementById(CACHE_SCRIPT_ID);
  if (script?.textContent) {
    try {
      applyParsedCache(JSON.parse(script.textContent));
    } catch {
      /* ignore malformed prefetch payload */
    }
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) applyParsedCache(JSON.parse(raw));
  } catch {
    /* private mode or quota */
  }
}

function readBannerCache(section) {
  if (!section) return undefined;
  if (bannerCache.has(section)) return bannerCache.get(section);
  hydrateCaches();
  if (bannerCache.has(section)) return bannerCache.get(section);
  return undefined;
}

function persistBannerCache() {
  const payload = JSON.stringify(Object.fromEntries(bannerCache));

  if (typeof document !== 'undefined') {
    let script = document.getElementById(CACHE_SCRIPT_ID);
    if (!script) {
      script = document.createElement('script');
      script.id = CACHE_SCRIPT_ID;
      script.type = 'application/json';
      document.body.appendChild(script);
    }
    script.textContent = payload;
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, payload);
  } catch {
    /* private mode or quota */
  }
}

/**
 * Fetches CMS banners for a page section. Use hasDynamicBanner to branch layout:
 * with banner → image banner first, white title band; without → red gradient title only.
 */
export function usePageBanners(section) {
  const [banners, setBanners] = useState(() => readBannerCache(section) ?? []);
  const [loading, setLoading] = useState(() => !!section && readBannerCache(section) === undefined);

  useEffect(() => {
    if (!section) {
      setBanners([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    if (readBannerCache(section) === undefined) {
      setLoading(true);
    }

    fetchBannersBySection(section).then((data) => {
      if (cancelled) return;
      const list = Array.isArray(data) ? data : [];
      bannerCache.set(section, list);
      persistBannerCache();
      setBanners(list);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [section]);

  const hasDynamicBanner = banners.length > 0;

  return {
    banners,
    loading,
    hasDynamicBanner,
    banner: banners[0],
  };
}
